import React from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Typography } from '@mui/joy';
import { SmsOutlined } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminCXAPI, cxError } from '../../../lib/apiCX';
import { AccountSection } from '../accounts/AccountSection';

export function AutomaticTexts({ customerId }: { customerId: string }) {
  const cache = useQueryClient();
  const key = ['admin', 'cx', 'automatic-texts', customerId];
  const query = useQuery({
    queryKey: key,
    queryFn: () => AdminCXAPI.automaticTexts(customerId),
    refetchInterval: 30000,
  });
  const pause = useMutation({
    mutationFn: (paused: boolean) => AdminCXAPI.pauseAutomaticTexts(customerId, paused),
    onSuccess: (data) => cache.setQueryData(key, data),
  });
  const state = query.data;
  return (
    <AccountSection
      title="Automatic follow-ups"
      description="Trial check-ins follow your Accounts settings and this account’s saved onboarding progress. Replies notify the CSM in ApexOS and by email, and pause further automatic texts."
      icon={<SmsOutlined />}
      action={
        state && (
          <Chip color={state.paused ? 'warning' : state.enabled ? 'success' : 'neutral'}>
            {state.paused
              ? 'Paused for this account'
              : state.enabled
                ? 'Enabled'
                : 'Off in Accounts settings'}
          </Chip>
        )
      }
    >
      {query.isPending && <LinearProgress />}
      {query.error && (
        <Alert color="danger">
          {cxError(query.error)}
          <Button onClick={() => query.refetch()}>Retry</Button>
        </Alert>
      )}
      {state && (
        <>
          <Typography level="body-sm">
            {state.paused
              ? state.pause_reason
              : state.waiting_reason ||
                'Ready for the next scheduled check-in. Actual sending also requires an unfinished onboarding step.'}
          </Typography>
          {state.paused && (
            <Typography level="body-sm">
              Review the latest reply in Timeline &amp; messages. Resuming allows the next eligible check-in;
              it does not send a message immediately.
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              color={state.paused ? 'primary' : 'neutral'}
              loading={pause.isPending}
              onClick={() => pause.mutate(!state.paused)}
            >
              {state.paused
                ? 'Resume automatic texts'
                : state.enabled
                  ? 'Pause for this account'
                  : 'Exclude this account from automatic texts'}
            </Button>
            <Button variant="plain" onClick={() => query.refetch()}>
              Refresh status
            </Button>
          </Box>
          {state.runs.length > 0 && (
            <Box component="details">
              <Typography component="summary" level="title-sm" sx={{ cursor: 'pointer' }}>
                Recent automatic texts ({state.runs.length})
              </Typography>
              <Box sx={{ mt: 1.5, display: 'grid', gap: 1.5 }}>
                {state.runs.map((run) => (
                  <Box key={run.id} sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}>
                    <Typography level="title-sm">
                      Trial day {run.day} · {run.status.replaceAll('_', ' ')}
                    </Typography>
                    <Typography level="body-xs">Scheduled {new Date(run.due_at).toLocaleString()}</Typography>
                    {run.detail && <Typography level="body-sm">{run.detail}</Typography>}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
      {pause.error && <Alert color="danger">{cxError(pause.error)}</Alert>}
    </AccountSection>
  );
}
